import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Field } from 'formik';
import _ from 'lodash';
import { LanguageType, AllLanguageTypes, getGroupedCountryOptions, Country } from '@tradingblock/types';
import {
  Section,
  FormGroup,
  StepperField,
  SelectDropdownField,
  Text,
  ContactEmail,
  ContactPhone,
} from '@tradingblock/components';
import { ApplicationFormSection } from '../../../../types';
import { useData } from '../../../../state/selectors';
import { AFFILIATES_WITH_REP_IDS, getHeardAbout, updateFieldsDependentOnOwnerCountry } from '../../../../services';
import config from '../../../../config';

export const UserInfoSection: React.FC<ApplicationFormSection> = ({
  getText,
  values,
  setValues,
  setActiveLanguage,
}) => {
  const authToken = useData(d => d.ui.apiToken);
  const showLanguageSelection = false;
  const siteGroup = process.env.REACT_APP_TB_SITE_GROUP;
  const affiliate = useData(d => d.ui.affiliate);

  const heardAbout = useData(d => d.ui.heardAbout);

  // Sort heardAbout to move "Auto-trade newsletter" (heardAboutId: 2) to the bottom
  const sortedHeardAbout = useMemo(() => {
    if (!heardAbout) return heardAbout;
    const autoTradeNewsletter = heardAbout.find(h => h.heardAboutId === 2);
    const otherItems = heardAbout.filter(h => h.heardAboutId !== 2);
    return autoTradeNewsletter ? [...otherItems, autoTradeNewsletter] : heardAbout;
  }, [heardAbout]);

  const heardAboutSelected = useMemo(() => getHeardAbout(heardAbout, values.hearAboutUsId), [values, heardAbout]);

  const heardAboutOptions = useMemo(() => _.map(sortedHeardAbout, h => `${h.heardAboutId}`), [sortedHeardAbout]);
  // if siteGroup is MB we need to filter out the "Other" option, automatically select the first option and disable the dropdown
  if (siteGroup === 'mb') {
    const otherIndex = _.findIndex(sortedHeardAbout, h => h.description === 'Other');
    if (otherIndex > -1) {
      heardAboutOptions.splice(otherIndex, 1);
    }
  }

  const [showAssistanceSection, setShowAssistanceSection] = useState<boolean>(false);

  useEffect(() => {
    if (siteGroup === 'mb' && heardAboutOptions.length > 0) {
      if (!values.hearAboutUsId) {
        setValues({
          ...values,
          hearAboutUsId: heardAboutOptions[0],
        });
      }
    }
  }, [heardAboutOptions, siteGroup, values, setValues]);

  const heardAboutOptionsLabels = useMemo(() => {
    return _.reduce(
      sortedHeardAbout,
      (acc: { [option: string]: string }, h) => ({ ...acc, [`${h.heardAboutId}`]: h.description }),
      {}
    );
  }, [sortedHeardAbout]);

  if (siteGroup === 'mb') {
    // remove the "Other" option from heardAboutSelected.representatives if it exists
    const otherIndex = _.findIndex(heardAboutSelected && heardAboutSelected.representatives, r => r.repId === 42);
    if (otherIndex > -1 && heardAboutSelected && heardAboutSelected.representatives) {
      heardAboutSelected.representatives.splice(otherIndex, 1);
    }
  }

  const representativesOptions = useMemo(
    () =>
      heardAboutSelected &&
      heardAboutSelected.representatives &&
      _.map(heardAboutSelected.representatives, r => `${r.repId}`),
    [heardAboutSelected]
  );
  const representativesOptionsLabels = useMemo(() => {
    if (heardAboutSelected && heardAboutSelected.representatives) {
      return _.reduce(
        heardAboutSelected.representatives,
        (acc: { [option: string]: string }, r) => ({ ...acc, [`${r.repId}`]: r.description }),
        {}
      );
    }
    return undefined;
  }, [heardAboutSelected]);

  const onHeardAboutChanged = useCallback(
    (hearAboutUsId: number) => {
      const heardAboutToSelect = getHeardAbout(heardAbout, hearAboutUsId);
      setValues({
        ...values,
        hearAboutUsId,
        repId: heardAboutToSelect && heardAboutToSelect.repId,
      });
    },
    [heardAbout, values, setValues]
  );

  const determineSecondaryHearAboutUsDropdownDefaultLabelId = (): string => {
    if (!values.hearAboutUsId) {
      return '';
    }

    let hearAboutUsIdInt = null;

    if (typeof values.hearAboutUsId === 'number') {
      hearAboutUsIdInt = values.hearAboutUsId;
    } else if (typeof values.hearAboutUsId === 'string') {
      hearAboutUsIdInt = parseInt(values.hearAboutUsId);
    }

    if (
      ((config.isDev || config.isLocal) && hearAboutUsIdInt === 38) ||
      ((config.isBeta || config.isProduction) && hearAboutUsIdInt === 37)
    ) {
      return 'selectInvestorType';
    }

    return '';
  };

  const isOwnerCountryLATAM =
    values.ownerCountry === Country.Argentina ||
    values.ownerCountry === Country.Chile ||
    values.ownerCountry === Country.Peru ||
    values.ownerCountry === Country.Uruguay;

  const isVirtual = config.isVirtual;

  // Helper function to determine if rep selection should be disabled
  const isRepSelectionDisabled = useMemo(() => {
    // If there's no affiliate, allow rep selection
    if (!affiliate) return false;

    // If affiliate is in AFFILIATES_WITH_REP_IDS, disable rep selection (auto-set rep)
    if (AFFILIATES_WITH_REP_IDS.includes(affiliate)) return true;

    // For affiliates not in AFFILIATES_WITH_REP_IDS (like EST),
    // allow rep selection even if repId is already set
    return false;
  }, [affiliate]);

  return (
    <Section>
      {/* <h2 className="fields-title">
        {getText(!authToken ? `registration${config.isVirtual ? '-virtual' : ''}` : 'authenticated')}
      </h2> */}
      <p className="mute">
        {!authToken && <>{getText('registration', 'description')} </>}
        {getText('registration.help', undefined, {
          data: {
            helpLink: (
              <button
                onClick={e => {
                  e.preventDefault();
                  setShowAssistanceSection(!showAssistanceSection);
                }}
              >
                {getText('registration.helpLink')}
              </button>
            ),
          },
        })}
      </p>
      {showAssistanceSection && (
        <>
          <h2 className="fields-title">
            <Text id="contact.content" />
          </h2>
          <p>
            <Text
              id="contact.content"
              textKey="description"
              siteGroup={siteGroup}
              data={{
                email: <ContactEmail siteGroup={siteGroup} />,
                phone: <ContactPhone siteGroup={siteGroup} />,
              }}
            />
          </p>
        </>
      )}
      {showLanguageSelection && (
        <FormGroup>
          <Field
            component={StepperField}
            id="languageType"
            options={AllLanguageTypes}
            onChanged={(languageType: LanguageType) => {
              setValues({
                ...values,
                languageType,
              });
              if (setActiveLanguage) {
                setActiveLanguage(languageType);
              }
            }}
          />
        </FormGroup>
      )}

      {!config.isVirtual && (
        <>
          <FormGroup>
            <Field
              component={SelectDropdownField}
              id="ownerCountry"
              options={getGroupedCountryOptions(values && values.clearer, true)}
              defaultLabelId="address.userCountry"
              optionsDefaultLabelId="country"
              onChanged={(ownerCountry: Country) =>
                setValues(updateFieldsDependentOnOwnerCountry(values, ownerCountry))
              }
            />
          </FormGroup>
          <FormGroup>
            <Field
              component={SelectDropdownField}
              id="hearAboutUsId"
              isSearchable={false}
              useRawOptions={true}
              options={heardAboutOptions}
              rawOptionLabels={heardAboutOptionsLabels}
              onChanged={onHeardAboutChanged}
              siteGroup={siteGroup}
              disabled={!!affiliate} // Locked when coming from affiliate link
            />
          </FormGroup>
          {representativesOptions && (
            <FormGroup>
              <Field
                component={SelectDropdownField}
                id="repId"
                isSearchable={false}
                useRawOptions={true}
                options={representativesOptions}
                rawOptionLabels={representativesOptionsLabels}
                defaultLabelId={determineSecondaryHearAboutUsDropdownDefaultLabelId()}
                disabled={isRepSelectionDisabled}
              />
            </FormGroup>
          )}
        </>
      )}
    </Section>
  );
};
